/**
 * Left sidebar of the workflow editor: catalog of node kinds the user
 * can drag onto the canvas, filtered by the workflow's direction.
 *
 * Drag uses HTML5 native dataTransfer with MIME type
 * ``application/x-tray-node-kind``. The canvas reads it in onDrop.
 *
 * Direction filtering rules (matches backend validator):
 *   inbound  → source / transform / control / io_canonical[WRITE_*] /
 *              lookup / plugin
 *   internal → transform / control / io_canonical[READ_*, WRITE_*, EMIT_EVENT] /
 *              lookup / plugin
 *   outbound → sink / transform / control / io_canonical[READ_*, EMIT_EVENT] /
 *              lookup / plugin
 *
 * "Read/write canonical" is split per direction so users don't have to
 * mentally model the constraints — the palette just hides incompatible
 * options.
 */
import * as React from "react";

import { NodeIconBadge } from "@/components/workflows/nodeIcons";
import type { CatalogNode, Direction, NodeKind, WorkflowKind } from "@/lib/api-workflows";

export interface NodePaletteProps {
  catalog: CatalogNode[];
  /** Workflow specialisation — drives the primary palette filter
   *  (canonical-only / orchestration-only nodes via ``node.kinds``). */
  kind: WorkflowKind;
  /** Canonical-only secondary filter: which direction the workflow
   *  has, used to hide source/sink kinds that don't make sense for
   *  that direction. Ignored for orchestration workflows. */
  direction?: Direction;
}

const CATEGORY_LABEL: Record<string, string> = {
  source: "Sources",
  sink: "Sinks",
  transform: "Transforms",
  control: "Control flow",
  io_canonical: "Canonical I/O",
  lookup: "Lookups",
  plugin: "Plugins",
};

const CATEGORY_ORDER = [
  "source",
  "sink",
  "transform",
  "control",
  "io_canonical",
  "lookup",
  "plugin",
];

function isAllowedByDirection(node: CatalogNode, direction: Direction): boolean {
  switch (direction) {
    case "inbound":
      if (node.category === "sink") return false;
      if (node.kind === "read_canonical") return false; // inbound has no canonical input
      return true;
    case "internal":
      if (node.category === "source" || node.category === "sink") return false;
      return true;
    case "outbound":
      if (node.category === "source") return false;
      if (node.kind === "write_canonical") return false; // outbound writes to external, not canonical
      return true;
  }
}

function isAllowedForKind(node: CatalogNode, kind: WorkflowKind): boolean {
  // ``kinds`` is optional in the type for back-compat with old API
  // responses; absence = both (the backend default).
  if (!node.kinds || node.kinds.length === 0) return true;
  return node.kinds.includes(kind);
}

export function NodePalette({ catalog, kind, direction }: NodePaletteProps) {
  const grouped = React.useMemo(() => {
    const map = new Map<string, CatalogNode[]>();
    catalog
      // 1) Top-level filter: node must be allowed in this workflow kind.
      .filter((n) => isAllowedForKind(n, kind))
      // 2) Canonical-only secondary filter: direction-aware
      //    source/sink restrictions. Orchestration skips this.
      .filter((n) =>
        kind === "canonical" && direction
          ? isAllowedByDirection(n, direction)
          : true,
      )
      .forEach((n) => {
        const arr = map.get(n.category) ?? [];
        arr.push(n);
        map.set(n.category, arr);
      });
    return map;
  }, [catalog, kind, direction]);

  return (
    <aside className="h-full w-56 shrink-0 overflow-y-auto border-r border-border bg-surface">
      <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">
        Palette
      </div>
      <div className="px-2 py-2">
        {CATEGORY_ORDER.map((cat) => {
          const nodes = grouped.get(cat);
          if (!nodes || nodes.length === 0) return null;
          return (
            <div key={cat} className="mb-3">
              <div className="px-1 pb-1 text-xs uppercase tracking-wider text-muted-foreground">
                {CATEGORY_LABEL[cat] ?? cat}
              </div>
              <ul className="space-y-1">
                {nodes.map((n) => (
                  <li key={n.kind}>
                    <button
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "application/x-tray-node-kind",
                          n.kind,
                        );
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      title={n.description}
                      className="flex w-full cursor-grab items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-left text-xs hover:border-accent hover:bg-accent/10 active:cursor-grabbing"
                    >
                      <NodeIconBadge kind={n.kind} category={n.category} size={24} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-foreground">{n.label}</div>
                        <div className="line-clamp-2 text-[11px] text-muted-foreground">
                          {n.description}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/** Lookup the kind → label/category mapping used by the canvas chips. */
export function buildCatalogIndex(
  catalog: CatalogNode[],
): Record<NodeKind, CatalogNode> {
  const idx: Partial<Record<NodeKind, CatalogNode>> = {};
  catalog.forEach((n) => {
    idx[n.kind] = n;
  });
  return idx as Record<NodeKind, CatalogNode>;
}
